import { useEffect, useMemo, useState } from 'react'
import { ImageIcon, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/utils'
import { LucideIcon, loadLucideIcons } from '@/lib/lucideIconRegistry'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/shadcn/popover'

interface IconPickerProps {
  value: string
  onChange: (value: string) => void
  suggestions?: string[]
  placeholder?: string
  ariaLabel?: string
}

const DEFAULT_SUGGESTIONS = [
  'message-circle',
  'life-buoy',
  'dollar-sign',
  'package',
  'target',
  'rocket',
  'help-circle',
  'phone',
  'mail',
  'wrench',
  'shield',
  'bug',
  'sparkles',
  'heart',
  'star',
]

export function IconPicker({
  value,
  onChange,
  suggestions = DEFAULT_SUGGESTIONS,
  placeholder = 'Select an icon',
  ariaLabel = 'Icon',
}: IconPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [names, setNames] = useState<string[] | null>(null)

  useEffect(() => {
    if (open && names === null) {
      loadLucideIcons().then((icons) => setNames(Object.keys(icons)))
    }
  }, [open, names])

  const results = useMemo(() => {
    if (!names) return []
    const q = query.trim().toLowerCase()
    if (!q) return []
    return names.filter((n) => n.includes(q)).slice(0, 96)
  }, [query, names])

  const handleSelect = (name: string) => {
    onChange(name)
    setOpen(false)
    setQuery('')
  }

  const handleClear = () => {
    onChange('')
    setOpen(false)
    setQuery('')
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setQuery('')
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          aria-expanded={open}
          className="w-full justify-between font-normal cursor-pointer"
        >
          <span className="flex items-center gap-2">
            {!value ? (
              <ImageIcon className="size-4 opacity-50" />
            ) : /^[a-z0-9-]+$/.test(value) ? (
              <LucideIcon name={value} className="size-5" />
            ) : (
              <span className="text-lg leading-none">{value}</span>
            )}
            <span className={cn(!value && 'text-muted-foreground')}>
              {value || placeholder}
            </span>
          </span>
          {value && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear icon"
              className="size-5 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleClear()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleClear()
                }
              }}
            >
              <X className="size-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-72 p-3"
        align="start"
      >
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search icons..."
          className="mb-3"
        />

        {query.trim() ? (
          names === null ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading...</div>
          ) : results.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No icons match "{query}"
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto -mx-1 px-1">
              <div className="grid grid-cols-8 gap-1">
                {results.map((name) => (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    onClick={() => handleSelect(name)}
                    className={cn(
                      'aspect-square flex items-center justify-center rounded hover:bg-muted cursor-pointer',
                      value === name && 'bg-primary/15 ring-1 ring-primary/40',
                    )}
                  >
                    <LucideIcon name={name} className="size-5" />
                  </button>
                ))}
              </div>
            </div>
          )
        ) : (
          <>
            <div className="text-xs text-muted-foreground mb-2">Suggested</div>
            <div className="grid grid-cols-5 gap-1 mb-3">
              {suggestions.map((name) => (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => handleSelect(name)}
                  className={cn(
                    'aspect-square flex items-center justify-center rounded hover:bg-muted cursor-pointer',
                    value === name && 'bg-primary/15 ring-1 ring-primary/40',
                  )}
                >
                  <LucideIcon name={name} className="size-5" />
                </button>
              ))}
            </div>
            <div className="text-xs text-muted-foreground">
              Type to search 1,900+ icons
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
